import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { 
  Save,
  Key,
  Phone,
  Bot,
  Sparkles,
  Shield,
  AlertCircle,
  CheckCircle,
  Send,
  Loader2
} from "lucide-react";
import { toast } from "@/components/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/LanguageContext";
import safeLocalStorage from "@/utils/safeStorage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const [settings, setSettings] = useState({
    telegram_api_id: '',
    telegram_api_hash: '',
    telegram_phone: '',
    telegram_connected: false,
    ai_enabled: true,
    auto_classify: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationStep, setVerificationStep] = useState(null); // 'code' | '2fa'
  const [verificationCode, setVerificationCode] = useState('');
  const [phoneCodeHash, setPhoneCodeHash] = useState('');
  const [twoFaPassword, setTwoFaPassword] = useState('');
  const [connectionStatus, setConnectionStatus] = useState(null);
  
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  const getAuthHeaders = () => {
    const token = safeLocalStorage.getItem('auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, { headers: getAuthHeaders() });
      setSettings(prev => ({
        ...prev,
        ...response.data,
        telegram_api_id: response.data.telegram_api_id || '',
        telegram_api_hash: response.data.telegram_api_hash || '',
        telegram_phone: response.data.telegram_phone || ''
      }));
      
      // Check connection status
      if (response.data.telegram_api_id && response.data.telegram_api_hash) {
        checkConnectionStatus();
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkConnectionStatus = async () => {
    try {
      const response = await axios.get(`${API}/telegram/status`, { headers: getAuthHeaders() });
      setConnectionStatus(response.data);
      if (response.data.authorized) {
        setSettings(prev => ({ ...prev, telegram_connected: true }));
      }
    } catch (error) {
      console.error("Error checking connection status:", error);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/settings`, settings, { headers: getAuthHeaders() });
      toast.success(t('success.saved'));
      // Check connection status after saving
      if (settings.telegram_api_id && settings.telegram_api_hash) {
        checkConnectionStatus();
      }
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setSaving(false);
    }
  };

  const handleSendCode = async () => {
    try {
      setVerifying(true);
      const response = await axios.post(`${API}/telegram/send-code`, {}, { headers: getAuthHeaders() });
      
      if (response.data.success) {
        setPhoneCodeHash(response.data.phone_code_hash);
        setVerificationStep('code');
        toast.success(isRTL ? 'تم إرسال رمز التحقق' : 'Verification code sent');
      } else {
        toast.error(response.data.error || t('errors.serverError'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    } finally {
      setVerifying(false);
    }
  };

  const handleVerifyCode = async () => {
    try {
      setVerifying(true);
      const response = await axios.post(`${API}/telegram/verify-code`, {
        code: verificationCode,
        phone_code_hash: phoneCodeHash
      }, { headers: getAuthHeaders() });
      
      if (response.data.success) {
        toast.success(isRTL ? 'تم التحقق بنجاح!' : 'Verification successful!');
        setVerificationStep(null);
        setVerificationCode('');
        setSettings(prev => ({ ...prev, telegram_connected: true }));
        checkConnectionStatus();
      } else if (response.data.needs_2fa) {
        setVerificationStep('2fa');
        toast.info(isRTL ? 'يرجى إدخال كلمة مرور التحقق بخطوتين' : 'Please enter your 2FA password');
      } else {
        toast.error(response.data.error || t('errors.serverError'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    } finally {
      setVerifying(false);
    }
  };

  const handleVerify2FA = async () => {
    try {
      setVerifying(true);
      const response = await axios.post(`${API}/telegram/verify-2fa`, {
        password: twoFaPassword
      }, { headers: getAuthHeaders() });
      
      if (response.data.success) {
        toast.success(isRTL ? 'تم التحقق بنجاح!' : 'Verification successful!');
        setVerificationStep(null);
        setTwoFaPassword('');
        setSettings(prev => ({ ...prev, telegram_connected: true }));
        checkConnectionStatus();
      } else {
        toast.error(response.data.error || t('errors.serverError'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    } finally {
      setVerifying(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(isRTL ? 'هل أنت متأكد من قطع الاتصال بـ Telegram؟' : 'Are you sure you want to disconnect from Telegram?')) {
      return;
    }
    
    try {
      setVerifying(true);
      const response = await axios.post(`${API}/telegram/disconnect`, {}, { headers: getAuthHeaders() });
      
      if (response.data.success) {
        toast.success(isRTL ? 'تم قطع الاتصال بنجاح' : 'Disconnected successfully');
        setSettings(prev => ({ ...prev, telegram_connected: false }));
        setConnectionStatus(null);
      } else {
        toast.error(response.data.error || t('errors.serverError'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-8 animate-fade-in max-w-3xl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-wide">{t('settings.title')}</h1>
        <p className="text-neutral-500 mt-1">{t('settings.subtitle')}</p>
      </div>

      {/* Telegram API Settings */}
      <div className="glass rounded-xl p-6 space-y-6">
        <div className="flex items-center gap-3 pb-4 border-b border-white/10">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
            <Bot size={20} className="text-cyan-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">{t('settings.telegram')}</h2>
            <p className="text-sm text-neutral-500">{isRTL ? 'بيانات الاتصال بـ Telegram MTProto' : 'Telegram MTProto connection data'}</p>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-yellow-400">{isRTL ? 'كيفية الحصول على API Credentials' : 'How to get API Credentials'}</h4>
              <ol className="text-sm text-neutral-400 mt-2 space-y-1 list-decimal list-inside">
                <li>{isRTL ? 'اذهب إلى' : 'Go to'} <a href="https://my.telegram.org" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">my.telegram.org</a></li>
                <li>{isRTL ? 'سجل دخولك برقم هاتفك' : 'Login with your phone number'}</li>
                <li>{isRTL ? 'اذهب إلى "API Development Tools"' : 'Go to "API Development Tools"'}</li>
                <li>{isRTL ? 'أنشئ تطبيقاً جديداً' : 'Create a new application'}</li>
                <li>{isRTL ? 'انسخ API_ID و API_HASH' : 'Copy API_ID and API_HASH'}</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-neutral-400 mb-2 block">
              <div className="flex items-center gap-2">
                <Key size={14} />
                {t('settings.apiId')}
              </div>
            </Label>
            <Input
              data-testid="api-id-input"
              type="text"
              placeholder="12345678"
              value={settings.telegram_api_id}
              onChange={(e) => setSettings({ ...settings, telegram_api_id: e.target.value })}
            />
          </div>

          <div>
            <Label className="text-neutral-400 mb-2 block">
              <div className="flex items-center gap-2">
                <Shield size={14} />
                {t('settings.apiHash')}
              </div>
            </Label>
            <Input
              data-testid="api-hash-input"
              type="password"
              placeholder="••••••••••••••••"
              value={settings.telegram_api_hash}
              onChange={(e) => setSettings({ ...settings, telegram_api_hash: e.target.value })}
            />
          </div>

          <div>
            <Label className="text-neutral-400 mb-2 block">
              <div className="flex items-center gap-2">
                <Phone size={14} />
                {t('common.phone')}
              </div>
            </Label>
            <Input
              data-testid="phone-input"
              type="tel"
              placeholder="+966XXXXXXXXX"
              value={settings.telegram_phone}
              onChange={(e) => setSettings({ ...settings, telegram_phone: e.target.value })}
              dir="ltr"
            />
          </div>
        </div>

        {/* Connection Status & Verification */}
        <div className="space-y-4">
          <div className={`flex items-center justify-between p-4 rounded-lg ${
            connectionStatus?.authorized 
              ? 'bg-green-500/10' 
              : 'bg-neutral-200 dark:bg-neutral-800'
          }`}>
            <div className="flex items-center gap-3">
              {connectionStatus?.authorized ? (
                <CheckCircle size={20} className="text-green-400" />
              ) : (
                <div className="status-offline"></div>
              )}
              <div>
                <span className={connectionStatus?.authorized 
                  ? 'text-green-400' 
                  : 'text-neutral-600 dark:text-neutral-500'}>
                  {connectionStatus?.authorized 
                    ? (isRTL ? 'متصل بـ Telegram' : 'Connected to Telegram') 
                    : (isRTL ? 'غير متصل' : 'Not Connected')}
                </span>
                {connectionStatus?.user && (
                  <p className="text-sm text-neutral-500">
                    @{connectionStatus.user.username || connectionStatus.user.first_name}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex gap-2">
              {connectionStatus?.authorized ? (
                <Button
                  data-testid="disconnect-telegram-btn"
                  onClick={handleDisconnect}
                  disabled={verifying}
                  variant="destructive"
                  className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
                >
                  {verifying ? (
                    <Loader2 size={16} className="animate-spin ml-2" />
                  ) : null}
                  {isRTL ? 'قطع الاتصال' : 'Disconnect'}
                </Button>
              ) : settings.telegram_api_id && settings.telegram_api_hash && (
                <Button
                  data-testid="verify-telegram-btn"
                  onClick={handleSendCode}
                  disabled={verifying || !settings.telegram_phone}
                  className="bg-cyan-500 hover:bg-cyan-600"
                >
                  {verifying ? (
                    <Loader2 size={16} className="animate-spin ml-2" />
                  ) : (
                    <Send size={16} className="ml-2" />
                  )}
                  {isRTL ? 'التحقق الآن' : 'Verify Now'}
                </Button>
              )}
            </div>
          </div>

          {/* Verification Code Input */}
          {verificationStep === 'code' && (
            <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4 space-y-3">
              <h4 className="font-semibold text-cyan-400">
                {isRTL ? 'أدخل رمز التحقق' : 'Enter Verification Code'}
              </h4>
              <p className="text-sm text-neutral-400">
                {isRTL ? 'تم إرسال رمز إلى تطبيق Telegram الخاص بك' : 'A code was sent to your Telegram app'}
              </p>
              <div className="flex gap-3">
                <Input
                  data-testid="verification-code-input"
                  type="text"
                  placeholder="12345"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  className="flex-1"
                  dir="ltr"
                />
                <Button
                  onClick={handleVerifyCode}
                  disabled={verifying || !verificationCode}
                  className="bg-cyan-500 hover:bg-cyan-600"
                >
                  {verifying ? <Loader2 size={16} className="animate-spin" /> : (isRTL ? 'تحقق' : 'Verify')}
                </Button>
              </div>
            </div>
          )}

          {/* 2FA Password Input */}
          {verificationStep === '2fa' && (
            <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg p-4 space-y-3">
              <h4 className="font-semibold text-violet-400">
                {isRTL ? 'التحقق بخطوتين' : 'Two-Factor Authentication'}
              </h4>
              <p className="text-sm text-neutral-400">
                {isRTL ? 'أدخل كلمة مرور التحقق بخطوتين' : 'Enter your 2FA password'}
              </p>
              <div className="flex gap-3">
                <Input
                  data-testid="2fa-password-input"
                  type="password"
                  placeholder="••••••••"
                  value={twoFaPassword}
                  onChange={(e) => setTwoFaPassword(e.target.value)}
                  className="flex-1"
                />
                <Button
                  onClick={handleVerify2FA}
                  disabled={verifying || !twoFaPassword}
                  className="bg-violet-500 hover:bg-violet-600"
                >
                  {verifying ? <Loader2 size={16} className="animate-spin" /> : (isRTL ? 'تحقق' : 'Verify')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI Settings */}
      <div className="glass rounded-xl p-6 space-y-6">
        <div className="flex items-center gap-3 pb-4 border-b border-white/10">
          <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
            <Sparkles size={20} className="text-violet-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">{isRTL ? 'إعدادات الذكاء الاصطناعي' : 'AI Settings'}</h2>
            <p className="text-sm text-neutral-500">{isRTL ? 'التصنيف الذكي للأعضاء' : 'Smart member classification'}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-black/30 rounded-lg">
            <div>
              <h4 className="font-medium">{isRTL ? 'تفعيل التصنيف الذكي' : 'Enable Smart Classification'}</h4>
              <p className="text-sm text-neutral-500">{isRTL ? 'استخدام AI لتصنيف الأعضاء حسب رسائلهم' : 'Use AI to classify members by their messages'}</p>
            </div>
            <Switch
              data-testid="ai-enabled-switch"
              checked={settings.ai_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, ai_enabled: checked })}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-black/30 rounded-lg">
            <div>
              <h4 className="font-medium">{isRTL ? 'التصنيف التلقائي' : 'Auto Classification'}</h4>
              <p className="text-sm text-neutral-500">{isRTL ? 'تصنيف الأعضاء الجدد تلقائياً' : 'Automatically classify new members'}</p>
            </div>
            <Switch
              data-testid="auto-classify-switch"
              checked={settings.auto_classify}
              onCheckedChange={(checked) => setSettings({ ...settings, auto_classify: checked })}
              disabled={!settings.ai_enabled}
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          data-testid="save-settings-btn"
          onClick={handleSave}
          disabled={saving}
          className="btn-primary px-8"
        >
          <Save size={18} className={isRTL ? 'ml-2' : 'mr-2'} />
          {saving ? t('common.loading') : t('settings.saveChanges')}
        </Button>
      </div>
    </div>
  );
}
